import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { environmentBase } from '../environments/environment.base';
import { isAuth0SessionDead } from '../utils/auth0-errors';
import { switchMap, take, catchError, throwError } from 'rxjs';

/**
 * Interceptor that attaches Auth0 bearer token to outgoing HTTP requests
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Skip token attachment for specific URLs (e.g., public APIs, Auth0 endpoints)
  if (shouldSkipToken(req.url)) {
    return next(req);
  }

  // Only add token to API requests (requests to our backend API)
  if (!shouldAddToken(req.url)) {
    return next(req);
  }

  return authService.getAccessTokenSilently().pipe(
    take(1),
    switchMap(token => {
      const clonedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
      return next(clonedRequest);
    }),
    catchError(error => {
      console.error('Failed to get access token:', error);
      if (isAuth0SessionDead(error)) {
        authService.login();
      }
      return throwError(() => error);
    })
  );
};

/**
 * Determines if token should be added to the request
 * Only adds token to requests to our backend API
 */
function shouldAddToken(url: string): boolean {
  // Check if URL is for our API (matches the API base URL)
  const apiUrl = environmentBase.apiUrl;
  return url.startsWith(apiUrl);
}

/**
 * Determines if token should be skipped for specific URLs
 */
function shouldSkipToken(url: string): boolean {
  // Skip Auth0 endpoints
  if (url.includes('auth0.com') || url.includes('auth0')) {
    return true;
  }

  // Add other URLs that should not have auth token attached
  const skipUrls: string[] = [
    // Example: '/api/public'
  ];

  return skipUrls.some(skipUrl => url.includes(skipUrl));
}


